import { AlertDialog, Button } from '@heroui/react';
import classNames from 'classnames';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import useScrimThemeColor from '@/hooks/use-scrim-theme-color';
import { pendingKeepsAppearanceClass } from '@/shared/components/Button';
import DynamicT from '@/shared/components/DynamicT';
import MatrixLoader from '@/shared/components/MatrixLoader';

import type { ModalProps } from './type';

type Props = ModalProps & {
  readonly isMobile?: boolean;
};

const ConfirmDialog = ({
  className,
  isOpen = false,
  isConfirmLoading = false,
  isCancelLoading = false,
  children,
  cancelText = 'action.cancel',
  confirmText = 'action.confirm',
  confirmTextI18nProps,
  cancelTextI18nProps,
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
  onConfirm,
  onClose,
  isMobile = false,
}: Props) => {
  useScrimThemeColor(isOpen);

  const { t } = useTranslation();
  const bodyId = useId();
  const isBusy = isConfirmLoading || isCancelLoading;
  const isAlert = !onConfirm;

  return (
    <AlertDialog.Backdrop
      isOpen={isOpen}
      isDismissable={shouldCloseOnOverlayClick && !isBusy}
      isKeyboardDismissDisabled={!shouldCloseOnEsc || isBusy}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AlertDialog.Container placement="center" size="sm">
        <AlertDialog.Dialog className={className} aria-describedby={bodyId}>
          <AlertDialog.Header>
            <AlertDialog.Icon status={isAlert ? 'warning' : 'accent'} />
            <AlertDialog.Heading>{t('description.reminder')}</AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Body
            id={bodyId}
            // Stable hook for the integration tests.
            data-testid="modal-content"
            className="text-sm text-muted [overflow-wrap:anywhere]"
          >
            {children}
          </AlertDialog.Body>
          <AlertDialog.Footer className={classNames(isMobile && '*:flex-1')}>
            <Button
              // Full-width touch targets on mobile match the page's 44px buttons.
              size={isMobile ? 'lg' : 'md'}
              variant={isAlert ? 'primary' : 'tertiary'}
              className={pendingKeepsAppearanceClass}
              isPending={isCancelLoading}
              isDisabled={isConfirmLoading}
              onPress={onClose}
            >
              {isCancelLoading && <MatrixLoader size="sm" />}
              <DynamicT forKey={cancelText} interpolation={cancelTextI18nProps} />
            </Button>
            {onConfirm && (
              <Button
                size={isMobile ? 'lg' : 'md'}
                className={pendingKeepsAppearanceClass}
                isPending={isConfirmLoading}
                isDisabled={isCancelLoading}
                onPress={onConfirm}
              >
                {isConfirmLoading && <MatrixLoader size="sm" />}
                <DynamicT forKey={confirmText} interpolation={confirmTextI18nProps} />
              </Button>
            )}
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
};

export default ConfirmDialog;
